import { Button } from 'antd'
import { MarkupEditor } from 'features/documentMarkup/editor/MarkupEditor'
import {
  containsRectangle,
  isValidRectangle,
  MARKUP_COORDINATE_ROUNDING_TOLERANCE,
  rectanglesOverlap,
} from 'features/documentMarkup/editor/markupGeometry'
import type {
  AnnotationGeometry,
  MarkupAnnotation,
  MarkupRectangle,
  MarkupRegion,
} from 'features/documentMarkup/editor/types'
import { reportImageMarkupSurface } from 'features/files/utils/reportMarkupSurface'
import type {
  DocumentDrawing,
  DocumentRegion,
} from 'features/libra/document/LibraDocumentContext'
import type {
  PixelImageDimensions,
  PixelRectanglePolicy,
} from 'features/libra/drawing/pixelRectangleValidation'
import {
  normalizedPixelMinimumSize,
  pixelImageRectangle,
  pixelRectangleFromBounds,
  pixelRectangleToNormalized,
  validateMeasuredPixelRectangle,
} from 'features/libra/drawing/pixelRectangleValidation'
import type { DocumentDrawingCommand } from 'features/libra/drawing/useDocumentDrawing'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import styled, { css } from 'styled-components'

type Props = {
  url: string
  documentId: string
  height?: number | '100%'
  liveZones: readonly DocumentRegion[]
  readOnly: boolean
  isLoading: boolean
  pixelPolicy?: DrawingPixelPolicy
  drawingCommand?: DocumentDrawingCommand
  onZoneDrawn?: (
    bounds: DocumentDrawing['drawing_bounds']
  ) => string | undefined
  onCancelDrawing?: () => void
  selectedRegionId?: string
  drawingResetKey?: number
  onSelectRegion?: (regionId: string | undefined) => void
  onUpdateRegion?: (
    regionId: string,
    bounds: DocumentDrawing['drawing_bounds']
  ) => void
  onRemoveRegion?: (regionId: string) => void
  onNaturalSize?: (
    size: { documentId: string; width: number; height: number } | undefined
  ) => void
}

type DrawingPixelPolicy = { error: string } | PixelRectanglePolicy

type ImageMeasure = {
  sourceKey: string
  width?: number
  height?: number
  failed?: true
}

type PixelEdges = { x: readonly number[]; y: readonly number[] }

const DEFAULT_COLOR = '#3B82F6'
const CREATE_OWNER_ID = 'zone-drawing-owner'
const CREATE_REGION_ID = 'zone-drawing-region'

const ViewerShell = styled.div<{ $height: number | '100%'; $drawing: boolean }>`
  position: relative;
  width: 100%;
  height: ${({ $height }) =>
    typeof $height === 'number' ? `${$height}px` : $height};
  max-height: 100%;
  min-height: 0;
  overflow: auto;

  ${({ $drawing }) =>
    $drawing &&
    css`
      [data-markup-page-frame] > [role='application'] {
        z-index: 1;
      }
    `}
`

const Status = styled.div`
  padding: 1rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const pixelRectangle = (
  geometry: AnnotationGeometry,
  width: number,
  height: number,
  pixelEdges: PixelEdges
): DocumentDrawing['drawing_bounds'] | undefined => {
  if (geometry.shape !== 'rectangle') return undefined
  const toPixel = (
    coordinate: number,
    dimension: number,
    edges: readonly number[]
  ) => {
    const nearestEdge = edges.reduce<{
      pixel: number
      distance: number
    } | null>((nearest, pixel) => {
      const distance = Math.abs(coordinate - pixel / dimension)
      return distance < (nearest?.distance ?? Infinity)
        ? { pixel, distance }
        : nearest
    }, null)
    return nearestEdge &&
      nearestEdge.distance <= MARKUP_COORDINATE_ROUNDING_TOLERANCE
      ? nearestEdge.pixel
      : coordinate * dimension
  }
  const bounds = {
    x_start: toPixel(geometry.x_min, width, pixelEdges.x),
    y_start: toPixel(geometry.y_min, height, pixelEdges.y),
    x_end: toPixel(geometry.x_max, width, pixelEdges.x),
    y_end: toPixel(geometry.y_max, height, pixelEdges.y),
  }
  return isValidRectangle(pixelRectangleFromBounds(bounds)) ? bounds : undefined
}

const isAcceptedPixelRectangle = (
  bounds: DocumentDrawing['drawing_bounds'],
  imageSize: PixelImageDimensions,
  boundary: MarkupRectangle | undefined,
  obstacles: readonly MarkupRectangle[]
) => {
  const rectangle = pixelRectangleFromBounds(bounds)
  return (
    !validateMeasuredPixelRectangle(rectangle, imageSize) &&
    (!boundary || containsRectangle(boundary, rectangle)) &&
    !obstacles.some((obstacle) => rectanglesOverlap(obstacle, rectangle))
  )
}

export const SmartImageViewer = ({
  url,
  documentId,
  height = 1000,
  liveZones,
  readOnly,
  isLoading,
  pixelPolicy: activePixelPolicy,
  drawingCommand,
  onZoneDrawn,
  onCancelDrawing,
  selectedRegionId,
  drawingResetKey,
  onSelectRegion,
  onUpdateRegion,
  onRemoveRegion,
  onNaturalSize,
}: Props) => {
  const sourceKey = JSON.stringify([documentId, url])
  const [imageMeasure, setImageMeasure] = useState<ImageMeasure>()

  const measure =
    imageMeasure?.sourceKey === sourceKey ? imageMeasure : undefined
  const naturalWidth = measure?.width
  const naturalHeight = measure?.height
  const surface =
    naturalWidth && naturalHeight
      ? reportImageMarkupSurface(
          { entry_id: documentId },
          {
            src: url,
            resetKey:
              drawingResetKey === undefined
                ? sourceKey
                : `${sourceKey}:${drawingResetKey}`,
            naturalWidth,
            naturalHeight,
          }
        )
      : undefined

  const zones = liveZones
  const visibleZones = useMemo(
    () => zones.filter((zone) => zone.zone_reference_image_id === documentId),
    [documentId, zones]
  )
  const currentImageRectangles = useMemo(
    () =>
      naturalWidth && naturalHeight
        ? visibleZones.flatMap((zone) => {
            const bounds = zone.drawing_bounds
            const pixelBounds = bounds && pixelRectangleFromBounds(bounds)
            return pixelBounds && isValidRectangle(pixelBounds)
              ? [
                  {
                    zone,
                    geometry: pixelRectangleToNormalized(pixelBounds, {
                      width: naturalWidth,
                      height: naturalHeight,
                    }),
                  },
                ]
              : []
          })
        : [],
    [naturalHeight, naturalWidth, visibleZones]
  )
  const selectedZone = visibleZones.find((zone) => zone.id === selectedRegionId)
  const drawingCommandIsActive = Boolean(
    drawingCommand &&
    drawingCommand.documentId === documentId &&
    drawingCommand.resetKey === drawingResetKey &&
    onZoneDrawn &&
    !readOnly &&
    !isLoading
  )
  const policyError =
    activePixelPolicy && 'error' in activePixelPolicy
      ? activePixelPolicy.error
      : activePixelPolicy && activePixelPolicy.documentId !== documentId
        ? 'Drawing geometry does not match the selected image.'
        : activePixelPolicy &&
            !('error' in activePixelPolicy) &&
            naturalWidth &&
            naturalHeight &&
            activePixelPolicy.boundary &&
            (!isValidRectangle(activePixelPolicy.boundary) ||
              !containsRectangle(
                pixelImageRectangle({
                  width: naturalWidth,
                  height: naturalHeight,
                }),
                activePixelPolicy.boundary
              ))
          ? 'The source rectangle extends outside the measured image.'
          : undefined
  const validPixelPolicy =
    activePixelPolicy && !('error' in activePixelPolicy) && !policyError
      ? activePixelPolicy
      : undefined
  const pixelEdges = {
    x: validPixelPolicy?.boundary
      ? [validPixelPolicy.boundary.x_min, validPixelPolicy.boundary.x_max]
      : [],
    y: validPixelPolicy?.boundary
      ? [validPixelPolicy.boundary.y_min, validPixelPolicy.boundary.y_max]
      : [],
  }
  validPixelPolicy?.obstacles.forEach(({ rectangle }) => {
    pixelEdges.x.push(rectangle.x_min, rectangle.x_max)
    pixelEdges.y.push(rectangle.y_min, rectangle.y_max)
  })
  const canDraw = Boolean(surface && drawingCommandIsActive && !policyError)
  const passiveBoundaries = useMemo(
    () =>
      new Map(
        currentImageRectangles
          .filter(
            ({ zone }) =>
              canDraw &&
              (zone.readOnly || zone.id === drawingCommand?.replacementRegionId)
          )
          .map(({ zone, geometry }) => [zone.id, geometry] as const)
      ),
    [canDraw, currentImageRectangles, drawingCommand?.replacementRegionId]
  )
  const annotations = useMemo<ReadonlyArray<MarkupAnnotation>>(
    () =>
      currentImageRectangles
        .filter(({ zone }) => !passiveBoundaries.has(zone.id))
        .map(({ zone, geometry }) => ({
          id: zone.id,
          ownerId: zone.id,
          label: zone.ai_suggested ? `✦ ${zone.title} (AI)` : zone.title,
          geometry,
          appearance: {
            color: zone.color || DEFAULT_COLOR,
            fillOpacity: zone.ai_suggested ? 0.1 : 0.2,
            strokeDasharray: zone.ai_suggested ? '6 4' : undefined,
          },
        })),
    [currentImageRectangles, passiveBoundaries]
  )
  const canEditRegion = Boolean(
    surface &&
    selectedZone &&
    !selectedZone.readOnly &&
    onUpdateRegion &&
    !policyError &&
    !readOnly &&
    !isLoading
  )
  const canSelectRegion = Boolean(surface && onSelectRegion && !isLoading)
  const currentValues = {
    sourceKey: sourceKey || undefined,
    resetKey: drawingResetKey,
    drawingToken: drawingCommand?.token,
    pixelPolicy: activePixelPolicy,
    onNaturalSize,
    canDraw,
    canEditRegion,
    selectedRegionId,
    naturalWidth,
    naturalHeight,
    pixelBoundary: validPixelPolicy?.boundary,
    pixelObstacles:
      validPixelPolicy?.obstacles.map(({ rectangle }) => rectangle) ?? [],
    pixelEdges,
    canSelectRegion,
    onZoneDrawn,
    onSelectRegion,
    onUpdateRegion,
    onRemoveRegion,
  }
  const liveAdapter = useRef(currentValues)
  liveAdapter.current = currentValues

  useLayoutEffect(() => {
    if (drawingCommand && policyError) onCancelDrawing?.()
  }, [drawingCommand, onCancelDrawing, policyError])

  useLayoutEffect(() => {
    liveAdapter.current.onNaturalSize?.(undefined)
  }, [sourceKey])
  useLayoutEffect(
    () => () => {
      liveAdapter.current.sourceKey = undefined
      liveAdapter.current.onNaturalSize?.(undefined)
    },
    []
  )

  useEffect(() => {
    let current = true
    liveAdapter.current.sourceKey = sourceKey
    setImageMeasure({ sourceKey })

    const invalidate = () => {
      current = false
      if (liveAdapter.current.sourceKey === sourceKey)
        liveAdapter.current.sourceKey = undefined
    }

    if (!url.trim() || !documentId.trim()) {
      setImageMeasure({ sourceKey, failed: true })
      return invalidate
    }

    const image = new Image()
    image.onload = () => {
      if (!current || liveAdapter.current.sourceKey !== sourceKey) return
      const { naturalWidth: width, naturalHeight: height } = image
      if (
        !Number.isFinite(width) ||
        !Number.isFinite(height) ||
        width <= 0 ||
        height <= 0
      ) {
        setImageMeasure({ sourceKey, failed: true })
        return
      }
      setImageMeasure({ sourceKey, width, height })
      liveAdapter.current.onNaturalSize?.({ documentId, width, height })
    }
    image.onerror = () => {
      if (current && liveAdapter.current.sourceKey === sourceKey)
        setImageMeasure({ sourceKey, failed: true })
    }
    image.src = url

    return () => {
      invalidate()
      image.onload = null
      image.onerror = null
    }
  }, [documentId, sourceKey, url])
  const editorSelectedRegionId = canDraw ? CREATE_REGION_ID : selectedZone?.id
  const editorRegions: MarkupRegion[] = [
    ...visibleZones.map((zone) => {
      const boundary = passiveBoundaries.get(zone.id)
      return {
        id: zone.id,
        label: zone.title,
        color: zone.color || DEFAULT_COLOR,
        ...(boundary ? { boundary } : {}),
      }
    }),
    ...(canDraw
      ? [{ id: CREATE_REGION_ID, label: 'New boundary', color: DEFAULT_COLOR }]
      : []),
  ]
  const rectangleConstraints = useMemo(() => {
    if (!validPixelPolicy || !naturalWidth || !naturalHeight) return undefined
    return {
      boundary: validPixelPolicy.boundary
        ? pixelRectangleToNormalized(validPixelPolicy.boundary, {
            width: naturalWidth,
            height: naturalHeight,
          })
        : undefined,
      obstacles: validPixelPolicy.obstacles.map(({ rectangle }) =>
        pixelRectangleToNormalized(rectangle, {
          width: naturalWidth,
          height: naturalHeight,
        })
      ),
    }
  }, [naturalHeight, naturalWidth, validPixelPolicy])
  const drawingIntent =
    canDraw && surface && drawingCommand
      ? {
          ownerId: CREATE_OWNER_ID,
          regionId: CREATE_REGION_ID,
          surfaceId: surface.id,
          tool: 'rectangle' as const,
          contextKey: drawingCommand.token,
        }
      : undefined

  const createAnnotation = (geometry: AnnotationGeometry) => {
    const current = liveAdapter.current
    if (
      current.sourceKey !== sourceKey ||
      current.resetKey !== drawingResetKey ||
      current.drawingToken !== drawingCommand?.token ||
      current.pixelPolicy !== activePixelPolicy ||
      !current.canDraw ||
      !current.naturalWidth ||
      !current.naturalHeight ||
      !current.onZoneDrawn
    )
      return { rejectedReason: 'Drawing is no longer active.' }
    const bounds = pixelRectangle(
      geometry,
      current.naturalWidth,
      current.naturalHeight,
      current.pixelEdges
    )
    if (!bounds)
      return { rejectedReason: 'Only valid rectangles are supported.' }
    if (
      !isAcceptedPixelRectangle(
        bounds,
        { width: current.naturalWidth, height: current.naturalHeight },
        current.pixelBoundary,
        current.pixelObstacles
      )
    )
      return { rejectedReason: 'The rectangle no longer fits its source.' }
    const acceptedAnnotationId = current.onZoneDrawn(bounds)
    return acceptedAnnotationId !== undefined
      ? { acceptedAnnotationId }
      : { rejectedReason: 'The drawing was not accepted.' }
  }

  const selectAnnotation = (annotationId: string | undefined) => {
    const current = liveAdapter.current
    if (
      current.sourceKey !== sourceKey ||
      current.resetKey !== drawingResetKey ||
      !current.canSelectRegion ||
      (annotationId && !zones.some((zone) => zone.id === annotationId))
    )
      return
    current.onSelectRegion?.(annotationId)
  }

  const updateAnnotation = (
    annotationId: string,
    geometry: AnnotationGeometry
  ) => {
    const current = liveAdapter.current
    if (
      current.sourceKey !== sourceKey ||
      current.resetKey !== drawingResetKey ||
      current.pixelPolicy !== activePixelPolicy ||
      !current.canEditRegion ||
      annotationId !== current.selectedRegionId ||
      !current.naturalWidth ||
      !current.naturalHeight
    )
      return
    const bounds = pixelRectangle(
      geometry,
      current.naturalWidth,
      current.naturalHeight,
      current.pixelEdges
    )
    if (
      bounds &&
      isAcceptedPixelRectangle(
        bounds,
        { width: current.naturalWidth, height: current.naturalHeight },
        current.pixelBoundary,
        current.pixelObstacles
      )
    )
      current.onUpdateRegion?.(annotationId, bounds)
  }

  const removeAnnotation = (annotationId: string) => {
    const current = liveAdapter.current
    if (
      current.sourceKey !== sourceKey ||
      current.resetKey !== drawingResetKey ||
      current.pixelPolicy !== activePixelPolicy ||
      !current.canEditRegion ||
      !current.onRemoveRegion ||
      annotationId !== current.selectedRegionId
    )
      return
    current.onRemoveRegion(annotationId)
  }

  if (!surface) {
    return (
      <ViewerShell $drawing={false} $height={height}>
        {policyError ? (
          <Status role='alert'>{policyError}</Status>
        ) : measure?.failed ? (
          <Status role='alert'>Unable to load the image dimensions.</Status>
        ) : (
          <Status aria-live='polite'>Loading image…</Status>
        )}
        {drawingCommand && onCancelDrawing && (
          <Button onClick={onCancelDrawing}>Cancel drawing</Button>
        )}
      </ViewerShell>
    )
  }

  return (
    <ViewerShell $drawing={canDraw} $height={height}>
      {policyError && <Status role='alert'>{policyError}</Status>}
      <MarkupEditor
        annotations={annotations}
        capabilities={{
          tools: canDraw
            ? ['rectangle']
            : selectedZone && canSelectRegion
              ? ['select']
              : [],
          canDraw,
          canEdit: canEditRegion,
          canDelete: Boolean(canEditRegion && onRemoveRegion),
          readOnly: !canDraw && !canEditRegion && !canSelectRegion,
        }}
        clampRectangleToSurface
        drawingIntent={drawingIntent}
        minimumRectangleSize={normalizedPixelMinimumSize(surface)}
        rectangleConstraints={rectangleConstraints}
        onCancelDrawing={canDraw ? onCancelDrawing : undefined}
        onCreateAnnotation={createAnnotation}
        onDeleteAnnotation={removeAnnotation}
        onSelectAnnotation={selectAnnotation}
        onUpdateAnnotation={updateAnnotation}
        regions={editorRegions}
        selectedOwnerId={canDraw ? CREATE_OWNER_ID : selectedZone?.id}
        selectedAnnotationId={canDraw ? undefined : selectedZone?.id}
        selectedRegionId={editorSelectedRegionId}
        surface={surface}
      />
    </ViewerShell>
  )
}
